// src/features/fourlink-config/hooks/useTurnoFilter.js

import { useState, useMemo, useRef, useEffect } from 'react';
import { normalizeTurnoId } from '../utils/storage/horarioStorage';
import { formatHorarioRange } from '../utils/helpers/formatters';

export const useTurnoFilter = (horariosDisponibles) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const dropdownRef = useRef(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }
    
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Limpiar selecciones que ya no existen
  useEffect(() => {
    const validIds = new Set(
      Array.isArray(horariosDisponibles)
        ? horariosDisponibles
            .map((horario) => normalizeTurnoId(horario?.id))
            .filter((id) => typeof id === 'string' && id)
        : []
    );

    setSelected((prev) => prev.filter((id) => validIds.has(id)));
  }, [horariosDisponibles]);

  // Crear opciones de filtro
  const options = useMemo(() => {
    return horariosDisponibles
      .map((horario) => {
        const turnoId = normalizeTurnoId(horario?.id);
        if (!turnoId) {
          return null;
        }
        const label = horario?.name?.trim() || 'Turno sin nombre';
        const rangeText = formatHorarioRange({
          startTime: horario?.startTime,
          endTime: horario?.endTime,
        });
        return {
          id: turnoId,
          label,
          rangeText,
          searchable: `${label} ${rangeText}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [horariosDisponibles]);

  // Filtrar opciones según búsqueda
  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return options;
    }
    return options.filter((option) => option.searchable.includes(query));
  }, [options, search]);

  // Etiquetas seleccionadas
  const selectedLabels = useMemo(() => {
    if (!selected.length) {
      return [];
    }
    const labelMap = new Map(options.map((option) => [option.id, option.label]));
    return selected.map((id) => labelMap.get(id)).filter(Boolean);
  }, [options, selected]);

  // Texto del botón
  const buttonText = useMemo(() => {
    if (selectedLabels.length === 0) {
      return 'Filtrar por turno';
    }
    if (selectedLabels.length <= 2) {
      return selectedLabels.join(', ');
    }
    const [first, second] = selectedLabels;
    const remaining = selectedLabels.length - 2;
    return `${first}, ${second} +${remaining}`;
  }, [selectedLabels]);

  const isActive = selected.length > 0;

  const toggle = () => setIsOpen((prev) => !prev);
  
  const toggleSelection = (turnoId) => {
    setSelected((prev) => {
      if (prev.includes(turnoId)) {
        return prev.filter((id) => id !== turnoId);
      }
      return [...prev, turnoId];
    });
  };

  const clear = () => {
    setSelected([]);
    setSearch('');
  };

  const close = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    search,
    selected,
    options,
    filteredOptions,
    selectedLabels,
    buttonText,
    isActive,
    dropdownRef,
    toggle,
    setSearch,
    toggleSelection,
    clear,
    close,
  };
};